// KbBlueprintPage.tsx — MJOS-040, MJOS-104 (redirect lookup)
//
// Generic renderer for any page_content row at slug `kb-doc/...` whose content
// is the BlockRenderer recipe shape ({ blocks: [...] }).
//
// Used by the catch-all route `/kb-doc/*` in CRM.tsx so authors can drop a
// new row in page_content and have it instantly reachable, without registering
// a bespoke React route per page.
//
// MJOS-101: top-right DownloadMenu (PDF + Markdown) — universal for every
// KB detail page. PDF is screenshot-based via html-to-image+jsPDF (matches
// what's on screen); Markdown is a clean per-block serialization of the
// recipe in `lib/download-md.ts`.
//
// MJOS-104: if the slug isn't found in page_content, the API checks
// page_redirects and may return { redirect_to: "<new-slug>" }. We follow it
// client-side via React Router replace() so old shared URLs keep working
// after a slug rename.
//
// MJOS-139: live reactive updates — subscribes to data-changed frames so a KB
// edit (create/update_kb_page) re-renders this page in place, no full reload.
//
// DEM-26: direction detection. A page renders RTL when (in priority order):
//   1. body.content.dir === "rtl" — explicit override in the recipe
//   2. body.nav_label OR JSON.stringify(blocks) contains Hebrew/Arabic chars
// We test the stringified recipe once (cheap, runs only when blocks change)
// instead of walking the props tree with an early-exit cap — a 200-string
// cap missed Hebrew that lived deep in long mixed pages like el-hadegel.
// When rtl, we pass `dir="rtl"` to BlockRenderer (it wraps its output in
// `<div dir="rtl">`) AND swap the font stack on the wrapper to Heebo /
// Assistant / Rubik (matching PitchDeckBlocks). Logical-property CSS in
// ArchitectureBlocks does the rest (border / padding / text-align flip).

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { BlockRenderer, type Block } from "./BlockRenderer";
import { architectureConfig, architectureT as T } from "./ArchitectureBlocks";
import { DownloadMenu } from "./DownloadMenu";
import { useApi } from "../../../lib/api";
import { useVoiceChannel } from "../../voice/VoiceChannelProvider";

// `T` is kept for the inline "Back to Knowledge base" link + error/loading
// palette references. Layout owns the page chrome (max-w-[1000px],
// responsive padding, sky-blue bg via NARROW_ROUTES + SKY_BLUE_ROUTES).

// DEM-26: Hebrew + Arabic + neighboring RTL script ranges.
// Covers: Hebrew (U+0590-05FF), Arabic (U+0600-06FF), Syriac (U+0700-074F),
// Thaana (U+0780-07BF), NKo (U+07C0-07FF), Samaritan / Mandaic / Arabic
// Supplement (U+0800-08FF), Arabic Presentation Forms-A (U+FB50-FDFF),
// Arabic Presentation Forms-B (U+FE70-FEFF).
//
// FIXED 2026-07-26: this used to be "one match → RTL", and the old comment
// claimed a false positive was "harmless visual variation". It is not. An
// English document that merely QUOTES a Hebrew term — a client's registered
// company name, an invoice recipient — had the ENTIRE page flipped, with
// punctuation landing at the start of every line and table columns reversed.
// Measure the PROPORTION of RTL letters instead: a genuinely Hebrew page is
// mostly Hebrew, an English page quoting a name is not.
const RTL_G = /[֐-׿؀-ۿ܀-ݏހ-޿߀-߿ࠀ-ࣿﭐ-﷿ﹰ-﻿]/g;
const LATIN_G = /[A-Za-z]/g;

// Share of letters that are RTL. Above this, the document itself is RTL.
const RTL_RATIO_THRESHOLD = 0.2;

function isRtlText(text: string): boolean {
  const rtl = (text.match(RTL_G) || []).length;
  if (rtl === 0) return false;
  const latin = (text.match(LATIN_G) || []).length;
  return rtl / (rtl + latin) > RTL_RATIO_THRESHOLD;
}

const HEBREW_FONT_STACK =
  "'Heebo', 'Assistant', 'Rubik', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

export const KbBlueprintPage = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { onDataChanged } = useVoiceChannel();
  const params = useParams();
  // React Router v6 wildcard match: params['*'] holds the path tail.
  const tail = params["*"] ?? "";
  const slug = tail ? `kb-doc/${tail}` : "";

  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [explicitDir, setExplicitDir] = useState<"ltr" | "rtl" | null>(null);
  const [navLabel, setNavLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setBlocks(null);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${slug}`);
        if (!res.ok) {
          const detail = await res.text().catch(() => "");
          if (!cancelled) {
            setError(`fetch failed: ${res.status} ${detail.slice(0, 200)}`);
          }
          return;
        }
        const body = (await res.json()) as {
          content?: { blocks?: Block[]; dir?: "ltr" | "rtl" };
          redirect_to?: string;
          nav_label?: string | null;
        };
        if (cancelled) return;

        // MJOS-104: server-side redirect lookup matched — follow client-side.
        // body.redirect_to is the full slug e.g. "kb-doc/max-security/closing/brief"
        // or "pitch-doc/max-security/eyal-pitch-deck". Replace history so the
        // back button skips the stale URL.
        if (body.redirect_to) {
          navigate(`/${body.redirect_to}`, { replace: true });
          return;
        }

        if (Array.isArray(body.content?.blocks)) {
          setBlocks(body.content?.blocks ?? null);
          setExplicitDir(body.content?.dir ?? null);
          setNavLabel(body.nav_label ?? null);
        } else {
          setError(
            `Page content at slug ${slug} is not a BlockRenderer recipe. Expected { blocks: [...] }.`,
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Live, reactive update: when the MCP write path broadcasts that THIS slug
  // changed (via the voice WS → VoiceChannelProvider), re-fetch and swap the
  // blocks IN PLACE — no full reload. This is the reactive binding for KB data,
  // the counterpart to the deploy-driven reload for code. (Pushed from the
  // write path; we never subscribe to Neon directly.)
  useEffect(() => {
    if (!slug) return;
    const unsubscribe = onDataChanged((changedSlug) => {
      if (changedSlug !== slug) return;
      (async () => {
        try {
          const res = await api(`/api/kb/${slug}`);
          if (!res.ok) return;
          const body = (await res.json()) as {
            content?: { blocks?: Block[]; dir?: "ltr" | "rtl" };
            nav_label?: string | null;
          };
          if (Array.isArray(body.content?.blocks)) {
            setBlocks(body.content?.blocks ?? null);
            setExplicitDir(body.content?.dir ?? null);
            setNavLabel(body.nav_label ?? null);
          }
        } catch {
          // Transient — the next change, or a manual refresh, will catch up.
        }
      })();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, onDataChanged]);

  // DEM-26: derive direction. Explicit override wins; otherwise sniff
  // Hebrew / Arabic chars in nav_label or the full stringified recipe.
  // JSON.stringify of the blocks is cheap (tens of KB) and runs only when
  // blocks change — simpler and more correct than walking props with a cap.
  const dir = useMemo<"ltr" | "rtl" | undefined>(() => {
    if (explicitDir) return explicitDir;
    if (navLabel && isRtlText(navLabel)) return "rtl";
    if (!blocks) return undefined;
    try {
      // Stringified blocks include English prop keys, which dilutes the ratio —
      // harmless, because a genuinely Hebrew page still lands far above the
      // threshold while an English page quoting a name sits near zero.
      if (isRtlText(JSON.stringify(blocks))) return "rtl";
    } catch {
      // Should not happen — props are JSON-shaped — but fall back to ltr.
    }
    return "ltr";
  }, [explicitDir, navLabel, blocks]);

  // Last path segment, used as the download filename stem.
  const filename = tail.split("/").pop() || "knowledge-base";

  return (
    <>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Link
          to="/knowledge-base"
          style={{
            color: T.accent,
            fontSize: 13,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ChevronLeft style={{ width: 14, height: 14 }} /> Knowledge base
        </Link>
        {blocks ? (
          <DownloadMenu
            pageRef={contentRef}
            blocks={blocks}
            filename={filename}
          />
        ) : null}
      </div>

      <div
        ref={contentRef}
        style={dir === "rtl" ? { fontFamily: HEBREW_FONT_STACK } : undefined}
      >
        {dir === "rtl" ? (
          <style>{`@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800&family=Assistant:wght@300;400;500;600;700&family=Rubik:wght@300;400;500;600;700&display=swap');`}</style>
        ) : null}
        {error ? (
          <div
            style={{
              padding: "20px 24px",
              fontSize: 14,
              color: T.red,
              background: T.redSoft,
              borderRadius: 8,
              border: `1px solid ${T.red}`,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Failed to load</div>
            <div style={{ fontSize: 13 }}>
              <code>{slug || "(no slug)"}</code>: {error}
            </div>
          </div>
        ) : blocks ? (
          <BlockRenderer
            config={architectureConfig}
            blocks={blocks}
            dir={dir}
          />
        ) : (
          <div style={{ padding: 24, fontSize: 14, color: T.ink3 }}>Loading…</div>
        )}
      </div>
    </>
  );
};

(KbBlueprintPage as unknown as { path: string }).path = "/kb-doc/*";
